import React, { useEffect, useRef, useState, useContext } from 'react';

import { useTranslation } from 'react-i18next';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'reac... Remove this comment to see the full error message
import { StripeProvider, Elements } from 'react-stripe-elements';

import { UserDataContext } from 'lane-shared/contexts';
import { ORDER_RECEIPT_FLOW } from 'lane-shared/helpers/constants/features/payment';
import { usePaymentAndMenuFeatureConfirmation } from 'lane-shared/hooks';
import { CreditCardType } from 'lane-shared/types/Stripe';
import { ContentType } from 'lane-shared/types/content/Content';
import { FeatureQuoteType } from 'lane-shared/types/payment/FeatureQuoteType';
import { PaymentMethod } from 'lane-shared/types/payment/PaymentAccount';
import { PaymentProviderEnum } from 'constants-payments';

import styles from 'components/features/ConfirmationModal.scss';

import { ErrorMessage, Loading } from 'components/general';
import { UserContentInteractionReceipt } from 'components/lane';

import useUserLocale from '../../../hooks/useUserLocale';
import Modal from '../../lds/Modal';
import { StripeFlows } from '../../payments/paymentAccountManagement/StripeConfigureButton';
import ModalCloseButton from '../Payment/ModalCloseButton';
import PaymentFeatureModalConfirmationMain, {
  PaymentFeatureModalConfirmationMainActions,
} from '../Payment/PaymentFeatureModalConfirmationMain';
import PaymentFeatureStripeAddCard, {
  PaymentFeatureStripeAddCardActions,
} from '../Payment/PaymentFeatureStripeAddCard';
import PaymentFeatureStripePaymentInformation from '../Payment/PaymentFeatureStripePaymentInformation';

type Props = {
  isOpen?: boolean;
  onClose?: () => void;
  content: ContentType;
  interaction: any;
  updateInteraction: (props: any) => any;
  onPaymentSuccess?: (...args: any) => void;
  onPaymentFailed?: (...args: any) => void;
  onPaymentFeatureUpdated?: (...args: any) => void;
  onBeforeSubmit?: (...args: any) => unknown;
};

function PaymentOrMenuFeatureConfirmation({
  isOpen,
  onClose,
  onPaymentSuccess,
  onPaymentFailed,
  onPaymentFeatureUpdated,
  onBeforeSubmit,
  content,
  interaction,
  updateInteraction,
}: Props) {
  const locale = useUserLocale();
  const { t } = useTranslation();
  const { user } = useContext(UserDataContext);
  const placePayRef = useRef({
    reject: () => null,
    resolve: () => null,
  });

  const [isPlacePayOpen, setIsPlacePayOpen] = useState(false);

  const userId = interaction.user?._id || user?._id;

  const [modalHistory, setModalHistory] = useState<any>([]);
  const [working, setWorking] = useState<boolean>(false);

  const handleBack = () => {
    setWorking(false);
    setModalHistory((prevModalHistory: any) => {
      if (modalHistory.length > 0) {
        return prevModalHistory.slice(0, -1);
      }

      return [];
    });
  };

  const {
    loading,
    submitting,
    quote,
    stripeApiKey,
    showReceipt,
    paymentFeature,
    paymentDetails,
    paymentAccounts,
    selectedPaymentAccount,
    setSelectedPaymentAccount,
    selectedCard,
    setSelectedCard,
    paymentMethods,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    essensysPaymentSettings,
    onSubmitInteraction,
    addPaymentMethod,
    removePaymentMethod,
    newCreatedInteraction,
    essensysError,
    isEssensys,
    error,
    onUpdateItem,
  } = usePaymentAndMenuFeatureConfirmation({
    contactId: undefined,
    locale,
    content,
    interaction,
    updateInteraction,
    userId,
    onBeforeSubmit,
    onPaymentFailed,
    onPaymentSuccess,
    onBeforePlacePayPayment: async () =>
      new Promise((resolve, reject) => {
        setIsPlacePayOpen(true);
        // @ts-expect-error ts-migrate(2322) FIXME: Type '(value: unknown) => void' is not assignable ... Remove this comment to see the full error message
        placePayRef.current.resolve = resolve;
        // @ts-expect-error ts-migrate(2322) FIXME: Type '(reason?: any) => void' is not assignable to... Remove this comment to see the full error message
        placePayRef.current.reject = reject;
      }),
  });

  const [initialQuote, setInitialQuote] = useState<FeatureQuoteType | null>(
    quote
  );

  useEffect(() => {
    setInitialQuote(quote);
  }, [quote]);

  useEffect(() => {
    if (showReceipt) {
      // TODO: move into onPaymentSuccess
      setModalHistory((prevModalHistory: any) => [
        ...prevModalHistory,
        ORDER_RECEIPT_FLOW,
      ]);
    }
  }, [showReceipt]);

  if (!content || !interaction || !paymentFeature) {
    return null;
  }

  const baseProps = {
    content,
    interaction,
    quote,
    essensysPaymentSettings,
    setModalHistory,
    onPaymentFailed,
    onPaymentFeatureUpdated,
    isEssensys,
    isPlacePayOpen,
    setIsPlacePayOpen,
    paymentAccounts,
    selectedPaymentAccount,
    setSelectedPaymentAccount,
    selectedCard,
    setSelectedCard,
    paymentDetails,
    paymentFeature,
    paymentMethods,
    selectedPaymentMethod,
    setSelectedPaymentMethod: async (paymentMethod: PaymentMethod) => {
      setSelectedPaymentMethod(paymentMethod);
    },
    onPaymentCancelled: () => {},
    essensysError,
    error,
    onUpdateItem,
    setInitialQuote: () => {},
  };

  const baseActionsProps = {
    onDone: handleBack,
    working,
  };

  const modalHistoryMap: {
    [key: string]: {
      title: string;
      component: React.ComponentType<any>;
      componentProps?: Record<any, any>;
      actions?: React.ComponentType<any>;
      actionsProps?: Record<any, any>;
    };
  } = {
    [StripeFlows.AddCard]: {
      title: t('Add a card'),
      component: PaymentFeatureStripeAddCard,
      actions: PaymentFeatureStripeAddCardActions,
      actionsProps: {
        onAddCard: async (token: any) => {
          setWorking(true);

          if (!selectedPaymentAccount) {
            throw Error(
              t(
                'A {{- name}} payment account must be selected in order to add a card.',
                {
                  name: 'Stripe',
                }
              )
            );
          }

          if (selectedPaymentAccount?.type === PaymentProviderEnum.Stripe) {
            const paymentMethod = await addPaymentMethod(
              selectedPaymentAccount._id,
              token.id
            );

            setSelectedPaymentMethod(paymentMethod);
          }

          setWorking(false);
        },
      },
    },
    [StripeFlows.ManageCards]: {
      title: t('Payment Options'),
      component: PaymentFeatureStripePaymentInformation,
      componentProps: {
        onAddCard: () => {
          setModalHistory((prevModalHistory: any) => [
            ...prevModalHistory,
            StripeFlows.AddCard,
          ]);
        },
        onRemoveCard: async (
          paymentAccountId: string | undefined,
          paymentMethod: CreditCardType
        ) => {
          if (paymentAccountId) {
            await removePaymentMethod(paymentAccountId, paymentMethod.id);
          } else {
            await window.Alert.alert({
              title: t('Could not remove card'),
              message: t(
                'The card could not be removed, please try again later.'
              ),
            });
          }
        },
        onNoPaymentMethods: async () => {
          setModalHistory((prevModalHistory: any) => {
            const newHistory = [...prevModalHistory];

            newHistory.pop();
            newHistory.push(StripeFlows.AddCard);

            return newHistory;
          });
        },
      },
    },
    [ORDER_RECEIPT_FLOW]: {
      title: t('Order Receipt'),
      actions: ModalCloseButton,
      actionsProps: { onClick: onClose },
      component: UserContentInteractionReceipt,
      componentProps: {
        interaction: newCreatedInteraction,
      },
    },
    default: {
      title: t('Complete your Purchase'),
      component: PaymentFeatureModalConfirmationMain,
      actions: PaymentFeatureModalConfirmationMainActions,
      actionsProps: {
        loading: submitting,
        purchaseDisabled:
          !selectedPaymentAccount ||
          submitting ||
          initialQuote?.total === 0 ||
          (initialQuote && initialQuote?.items.length < 1),
        onSubmit: () => {
          setWorking(true);

          try {
            onSubmitInteraction();
          } catch (err) {
            setWorking(false);

            throw err;
          }
        },
      },
    },
  };

  const currentHistory = modalHistory[modalHistory.length - 1] ?? 'default';

  const {
    component: CurrentComponent,
    title,
    actions: CurrentActions,
    actionsProps,
    componentProps,
  } = modalHistoryMap[currentHistory];

  const stripeProviderProps = stripeApiKey
    ? {
        apiKey: stripeApiKey,
      }
    : { stripe: null };

  const modalContent = (
    <CurrentComponent {...baseProps} {...(componentProps || {})} />
  );

  return (
    <StripeProvider key={stripeApiKey} {...stripeProviderProps}>
      <Elements>
        <Modal
          className={styles.confirmationModal}
          // @ts-expect-error ts-migrate(2322) FIXME: Type 'boolean | undefined' is not assignable to ty... Remove this comment to see the full error message
          isOpen={isOpen}
          // @ts-expect-error ts-migrate(2322) FIXME: Type '(() => void) | undefined' is not assignable ... Remove this comment to see the full error message
          onClose={!submitting ? onClose : () => null}
          onBack={!showReceipt ? handleBack : undefined}
          modalHistory={modalHistory}
          title={title}
          actions={
            CurrentActions ? (
              <>
                {loading ? (
                  <Loading />
                ) : (
                  <CurrentActions
                    {...baseActionsProps}
                    {...(actionsProps || {})}
                  />
                )}
              </>
            ) : undefined
          }
        >
          {error && <ErrorMessage error={error} />}
          {loading ? <Loading /> : modalContent}
        </Modal>
      </Elements>
    </StripeProvider>
  );
}

export default PaymentOrMenuFeatureConfirmation;
